import type { z } from 'zod'
import { ApiError, describeErrorBody } from './api-error'
import { contentDispositionFileName } from './content-disposition'

type QueryValue = string | number | boolean | null | undefined
/** A list is sent as a repeated parameter: `{ ids: ['a', 'b'] }` → `?ids=a&ids=b`. */
export type QueryParams = Record<string, QueryValue | readonly QueryValue[]>

export interface RequestOptions<TSchema extends z.ZodType> {
  /** Response contract. The response is rejected with a `validation` ApiError if it does not match. */
  schema: TSchema
  query?: QueryParams
  body?: unknown
  signal?: AbortSignal
  timeoutMs?: number
  headers?: Record<string, string>
  /** `false` for public endpoints (sign in, renewal, sign out): no access token, no renewal. */
  auth?: false
}

/** A file answer (`getFile`): no response contract, no body sent. */
export type FileRequestOptions = Omit<RequestOptions<z.ZodType>, 'schema' | 'body'>

export interface FileResponse {
  blob: Blob
  /** From `Content-Disposition` (`filename*` first); `null` without one. */
  fileName: string | null
}

/** Outcome of a session renewal. `failed` is a technical failure: the session may still be valid. */
export type RenewResult =
  { outcome: 'renewed' } | { outcome: 'ended' } | { outcome: 'failed'; error: unknown }

/** Connects the client to the session of the signed-in user (implemented in shared/auth). */
export interface HttpAuth {
  getAccessToken(): string | null
  /** Renews the session; concurrent callers share one renewal. */
  renew(): Promise<RenewResult>
  /** The session is over and the user has to sign in again. */
  onSessionEnded(): void
}

type Method = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'

/** Options of one call: the response is read by `read` once it is known to be a success. */
interface SendOptions<T> extends Omit<RequestOptions<z.ZodType>, 'schema'> {
  accept: string
  read: (response: Response, base: { method: Method; url: string; path: string }) => Promise<T>
}

export interface HttpClient {
  readonly baseUrl: string
  get<S extends z.ZodType>(path: string, options: RequestOptions<S>): Promise<z.infer<S>>
  post<S extends z.ZodType>(path: string, options: RequestOptions<S>): Promise<z.infer<S>>
  put<S extends z.ZodType>(path: string, options: RequestOptions<S>): Promise<z.infer<S>>
  patch<S extends z.ZodType>(path: string, options: RequestOptions<S>): Promise<z.infer<S>>
  delete<S extends z.ZodType>(path: string, options: RequestOptions<S>): Promise<z.infer<S>>
  /**
   * A file the backend answers itself (a CSV template, …), with the access token: a link cannot
   * send it. An error answer is JSON and rejects with an `ApiError` as for any other request.
   */
  getFile(path: string, options?: FileRequestOptions): Promise<FileResponse>
}

export interface HttpClientOptions {
  /** From runtime config, e.g. `/api` (same origin) or `https://mes.plant.local/api`. */
  baseUrl: string
  timeoutMs?: number
  fetchImpl?: typeof fetch
  /** Without it every request is sent as a public one. */
  auth?: HttpAuth
  /** The user's chosen language, sent as `X-Language` so the backend answers in it. */
  getLanguage?: () => string
}

export const DEFAULT_TIMEOUT_MS = 15_000

export function createHttpClient(options: HttpClientOptions): HttpClient {
  const { baseUrl, timeoutMs = DEFAULT_TIMEOUT_MS, auth } = options
  const doFetch = options.fetchImpl ?? ((input, init) => fetch(input, init))

  /**
   * Protected requests carry the access token. A 401 renews the session and repeats the request
   * once; a session that cannot be renewed, or a second 401, ends it.
   */
  async function request<T>(method: Method, path: string, opts: SendOptions<T>): Promise<T> {
    if (!auth || opts.auth === false) return send(method, path, opts, null)

    const renewedToken = async (rejection?: ApiError): Promise<string> => {
      const result = await auth.renew()
      const token = auth.getAccessToken()
      if (result.outcome === 'renewed' && token !== null) return token
      if (result.outcome === 'failed') throw result.error
      auth.onSessionEnded()
      throw (
        rejection ??
        new ApiError({
          kind: 'http',
          status: 401,
          code: 'not_authenticated',
          method,
          url: buildUrl(baseUrl, path, opts.query),
          message: `${method} ${path} needs a signed-in user`,
        })
      )
    }

    // After a page reload there is no access token in memory yet.
    const token = auth.getAccessToken() ?? (await renewedToken())
    try {
      return await send(method, path, opts, token)
    } catch (error) {
      if (!isUnauthorized(error)) throw error
      // A request rejected at the same time may already have renewed the session.
      const current = auth.getAccessToken()
      const retryToken = current !== null && current !== token ? current : await renewedToken(error)
      try {
        return await send(method, path, opts, retryToken)
      } catch (retryError) {
        if (isUnauthorized(retryError)) auth.onSessionEnded()
        throw retryError
      }
    }
  }

  async function send<T>(
    method: Method,
    path: string,
    opts: SendOptions<T>,
    accessToken: string | null,
  ): Promise<T> {
    const url = buildUrl(baseUrl, path, opts.query)
    const headers: Record<string, string> = { Accept: opts.accept }
    if (options.getLanguage) headers['X-Language'] = options.getLanguage()
    Object.assign(headers, opts.headers)
    if (accessToken !== null) headers.Authorization = `Bearer ${accessToken}`
    let body: string | undefined
    if (opts.body !== undefined) {
      headers['Content-Type'] = 'application/json'
      body = JSON.stringify(opts.body)
    }

    // AbortSignal.timeout / AbortSignal.any are not available on Chromium 100.
    const controller = new AbortController()
    const abortState = { timedOut: false }
    const timer = setTimeout(() => {
      abortState.timedOut = true
      controller.abort()
    }, opts.timeoutMs ?? timeoutMs)
    const onExternalAbort = () => {
      controller.abort()
    }
    opts.signal?.addEventListener('abort', onExternalAbort)
    if (opts.signal?.aborted) controller.abort()

    const base = { method, url }
    let response: Response
    try {
      response = await doFetch(url, {
        method,
        headers,
        body,
        credentials: 'same-origin',
        signal: controller.signal,
      })
    } catch (cause) {
      if (abortState.timedOut) {
        throw new ApiError({
          ...base,
          kind: 'timeout',
          message: `${method} ${path} timed out`,
          cause,
        })
      }
      if (controller.signal.aborted) {
        throw new ApiError({
          ...base,
          kind: 'aborted',
          message: `${method} ${path} was aborted`,
          cause,
        })
      }
      throw new ApiError({
        ...base,
        kind: 'network',
        message: `${method} ${path} failed: network error`,
        cause,
      })
    } finally {
      clearTimeout(timer)
      opts.signal?.removeEventListener('abort', onExternalAbort)
    }

    if (!response.ok) {
      const { detail, message, code } = describeErrorBody(await readBody(response))
      throw new ApiError({
        ...base,
        kind: 'http',
        status: response.status,
        code,
        detail,
        message: message ?? `${method} ${path} failed with HTTP ${response.status}`,
      })
    }
    return opts.read(response, { method, url, path })
  }

  /** JSON answer checked against its contract. */
  const json =
    <S extends z.ZodType>(schema: S) =>
    async (
      response: Response,
      base: { method: Method; url: string; path: string },
    ): Promise<z.infer<S>> => {
      const parsed = schema.safeParse(await readBody(response))
      if (parsed.success) return parsed.data
      const { method, url, path } = base
      const issues = parsed.error.issues.map((i) => `${i.path.join('.') || '(root)'}: ${i.message}`)
      if (import.meta.env.DEV) console.warn(`[api] contract violation ${method} ${path}`, issues)
      throw new ApiError({
        method,
        url,
        kind: 'validation',
        status: response.status,
        issues,
        message: `${method} ${path} returned data that does not match its contract`,
      })
    }

  const jsonCall =
    (method: Method) =>
    <S extends z.ZodType>(path: string, { schema, ...opts }: RequestOptions<S>) =>
      request(method, path, { ...opts, accept: 'application/json', read: json(schema) })

  return {
    baseUrl,
    get: jsonCall('GET'),
    post: jsonCall('POST'),
    put: jsonCall('PUT'),
    patch: jsonCall('PATCH'),
    delete: jsonCall('DELETE'),
    getFile: (path, opts = {}) =>
      request('GET', path, {
        ...opts,
        accept: '*/*',
        read: async (response) => ({
          blob: await response.blob(),
          fileName: contentDispositionFileName(response.headers.get('Content-Disposition')),
        }),
      }),
  }
}

function isUnauthorized(error: unknown): error is ApiError {
  return error instanceof ApiError && error.kind === 'http' && error.status === 401
}

async function readBody(response: Response): Promise<unknown> {
  if (response.status === 204) return undefined
  const text = await response.text()
  if (text.length === 0) return undefined
  const contentType = response.headers.get('Content-Type') ?? ''
  if (!contentType.includes('json')) return text
  try {
    return JSON.parse(text) as unknown
  } catch {
    return text
  }
}

export function buildUrl(baseUrl: string, path: string, query?: QueryParams): string {
  const joined = `${baseUrl.replace(/\/+$/, '')}/${path.replace(/^\/+/, '')}`
  const url = new URL(joined, globalThis.location.origin)
  for (const [key, given] of Object.entries(query ?? {})) {
    const values: readonly QueryValue[] = Array.isArray(given) ? given : [given]
    for (const value of values) {
      if (value !== undefined && value !== null) url.searchParams.append(key, String(value))
    }
  }
  return url.toString()
}
